/**
 * ProjectAssignmentField
 * "Tilldelad" metadata field for a project. Multi-assignee: lists every internal
 * assignee and every external delegate (each with its own copy-link), or
 * "Otilldelad", with an "(Ändra)" action that opens the assignment modal.
 *
 * Reads via getAssignees/getDelegations (tolerant of legacy scalars).
 */
import React, { useState } from 'react';
import { Button, Badge } from 'react-bootstrap';
import { User } from 'lucide-react';
import { ProjectWithBuilding } from '../../../api/projectsApi';
import { Userprofile } from '../../../Helpers/UsersContext';
import { ExternalUser, GuestLinkValidityPayload } from '../../../types/guestAccess';
import { AssignmentEmailOptions } from '../../../hooks/useAssignmentEmailOptions';
import { useDelegates } from '../../../hooks/useDelegates';
import { guestTokensApi } from '../../../api/guestTokensApi';
import { getAssignees, getDelegations } from '../../../types/assignees';
import CopyGuestLinkButton from '../../../Components/CopyGuestLinkButton';
import { copyableLinkUrl } from '../../../utils/linkValidity';
import AssignProjectModal from './AssignProjectModal';

interface ProjectAssignmentFieldProps {
    project: ProjectWithBuilding;
    buildingKey: string;
    internalUsers: Userprofile[];
    /** External users, forwarded to the assign modal to avoid a redundant fetch */
    externalUsers?: ExternalUser[];
    isAdmin: boolean;
    getUserName: (uid: string) => string;
    onAssignInternal: (userId: string | null) => Promise<void>;
    onAddInternal: (userId: string, sendEmail?: boolean) => Promise<void>;
    onRemoveInternal: (userId: string) => Promise<void>;
    onProjectChanged: () => Promise<void> | void;
    onRefetchUsers?: () => void;
}

const ProjectAssignmentField: React.FC<ProjectAssignmentFieldProps> = ({
    project,
    buildingKey,
    internalUsers,
    externalUsers,
    isAdmin,
    getUserName,
    onAssignInternal,
    onAddInternal,
    onRemoveInternal,
    onProjectChanged,
    onRefetchUsers,
}) => {
    const [showModal, setShowModal] = useState(false);

    const assignees = getAssignees(project);
    const delegations = getDelegations(project);
    const assigneeUids = Object.keys(assignees);
    const delegateUids = Object.keys(delegations);
    const isUnassigned = assigneeUids.length === 0 && delegateUids.length === 0;
    // Link state and copyable URL per delegate (admin callable; the field is
    // read-only for everyone else).
    const delegates = useDelegates('project', project.key, isAdmin && delegateUids.length > 0);

    const handleAssignExternal = async (
        externalUser: ExternalUser,
        emailOptions: AssignmentEmailOptions,
        linkValidity: GuestLinkValidityPayload
    ) => {
        // Additive: backend writes delegations[externalUserId] without touching
        // other delegates or internal assignees.
        await guestTokensApi.create({
            taskType: 'project',
            taskKey: project.key,
            buildingKey,
            externalUserId: externalUser.uid,
            sendEmail: true,
            ...emailOptions,
            ...linkValidity,
        });
    };

    // The change is already saved; a failed reload must not surface as an unhandled rejection.
    const handleAssigned = async () => {
        try {
            await onProjectChanged();
        } catch (err) {
            console.error('Error reloading the project after an assignment change:', err);
        }
        await delegates.refetch();
    };

    return (
        <div className="editable-date-field display" style={{ gridColumn: '1 / -1' }}>
            <div
                className="editable-date-label"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#000', fontSize: '14px', fontWeight: 700 }}
            >
                <User size={14} />Tilldelad
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginTop: '1px', paddingLeft: '20px' }}>
                {isUnassigned ? (
                    <span style={{ fontSize: '16px' }}>Otilldelad</span>
                ) : (
                    <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', fontSize: '16px' }}>
                        {assigneeUids.map((uid) => (
                            <Badge key={uid} bg="light" text="dark" className="border">
                                {getUserName(uid)}
                            </Badge>
                        ))}
                        {delegateUids.map((uid) => (
                            <span key={uid} className="d-flex align-items-center gap-1">
                                <Badge bg="secondary">Extern</Badge>
                                {getUserName(uid)}
                                {isAdmin && <CopyGuestLinkButton url={copyableLinkUrl(delegates.byUid[uid]?.link)} />}
                            </span>
                        ))}
                    </span>
                )}
                {isAdmin && (
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-2"
                        onClick={() => setShowModal(true)}
                    >
                        (Ändra)
                    </Button>
                )}
            </div>

            <AssignProjectModal
                show={showModal}
                onHide={() => setShowModal(false)}
                internalUsers={internalUsers}
                externalUsers={externalUsers}
                assigneeUids={assigneeUids}
                delegations={delegations}
                delegatesByUid={delegates.byUid}
                projectKey={project.key}
                projectTitle={project.title}
                projectDueDate={project.plannedEndDate}
                buildingName={project.buildingName}
                getUserName={getUserName}
                onAssignInternal={onAssignInternal}
                onAddInternal={onAddInternal}
                onRemoveInternal={onRemoveInternal}
                onAssignExternal={handleAssignExternal}
                onRevokeExternal={(externalUserId) => guestTokensApi.revoke({ taskType: 'project', taskKey: project.key, externalUserId })}
                onAssigned={() => { void handleAssigned(); }}
                onExternalUserCreated={onRefetchUsers}
            />
        </div>
    );
};

export default ProjectAssignmentField;
